import type {
  ActivityEvent,
  AdminFamily,
  AdminFamilyWeeks,
  AdminPantry,
  AssistantMessage,
  AssistantThread,
  Bootstrap,
  BroadcastAudience,
  BroadcastRecipientRow,
  BroadcastSummary,
  Category,
  Child,
  DayStatus,
  EmailLogEntry,
  FamilyPantryDiff,
  Feedback,
  InvitationInfo,
  ItemAllergen,
  NewProfileAllergen,
  ProfileAllergen,
  Item,
  Meal,
  NewFamily,
  Person,
  Proposal,
  ProposedChange,
  ReminderSettings,
  Session,
  SiblingClash,
  ShoppingExtra,
  ShoppingList,
  ShoppingState,
  SignupConfig,
  StarterAdminItem,
  StarterItem,
  Week,
  WeekSummary,
} from '@shared/types'

/** A broadcast as it is being composed, before anything is sent. */
export interface BroadcastDraft {
  subject: string
  markdown: string
  audience: BroadcastAudience
}

/** What the server makes of a draft: how it reads, and who would get it. */
export interface BroadcastPreview {
  subject: string
  /** The whole email, shell and all, for the phone-sized preview frame. */
  html: string
  text: string
  /** Just the rendered body, for the inline preview. */
  blocks: string
  audience: BroadcastAudience
  recipients: number
  /** Who exactly, up to 200 -- addresses included, so a filter can be checked. */
  sample: { name: string; email: string }[]
}

/** One suggested row from a bulk paste, before anything is created. */
export interface BulkRow {
  name: string
  categoryId: string
  emoji: string
  /** An item already in the pantry with this name, if there is one. */
  duplicateOf: string | null
}

/** What the weekly check decided, when asked to run on demand. */
export interface ReminderRun {
  skipped?: 'disabled' | 'not-the-hour' | 'already-planned' | 'no-recipients'
  weekStart?: string
  /** The children the nudge was about: the ones with an empty week. */
  nudgedFor?: string[]
  deliveries?: Delivery[]
}

/** What one notification did, as reported by the endpoint that triggered it. */
export interface Delivery {
  recipient: string
  ok: boolean
  duplicate?: boolean
  error?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** A stable code for the cases the client acts on, e.g. `needs_family`. */
    readonly code: string | null = null,
    /**
     * The whole error body, for the answers that carry detail rather than
     * just a message -- the sibling copy's list of things to check, say.
     */
    readonly body: unknown = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/* ---- Which child ---------------------------------------------------------
 *
 * The server takes `?profile=` on anything about one child and falls back to
 * the eldest without it. This is the one place the app puts it on, mirroring
 * the single chokepoint the server resolves it in.
 *
 * It goes on *every* API request rather than a list of the profile-scoped
 * ones, deliberately. A list is a thing to forget, and forgetting one means
 * the app quietly talks about the eldest while the screen names someone else
 * -- which for allergens is the one failure this app must never have. The
 * cost is that family-level data like the pantry gets a distinct cache entry
 * per child, which is some duplication in a cache and nothing worse.
 *
 * Kept in localStorage so the printable card, opened in its own tab, shows
 * the child the app was showing.
 */
const CHILD_KEY = 'fl_child'

/**
 * Which child the app is about.
 *
 * `?profile=` on the URL wins, because that is how the weekly reminder points
 * at the child it is nudging about: an email saying Bo's week is empty has to
 * open Bo's board, not whichever one this device last looked at. It is then
 * taken off the address bar -- left there, it would override the switcher on
 * every reload and quietly pin the app to one child.
 *
 * An id that no longer exists, or belongs to another family, is a 404 the
 * first request clears by retrying as the eldest.
 */
let activeChild = (() => {
  try {
    const url = new URL(window.location.href)
    const asked = url.searchParams.get('profile')
    if (asked) {
      // Remembered if it can be, but honoured either way: a private window
      // that refuses storage should still open the child the link named.
      try {
        localStorage.setItem(CHILD_KEY, asked)
      } catch {
        /* this page only, then */
      }
      url.searchParams.delete('profile')
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
      return asked
    }
    return localStorage.getItem(CHILD_KEY) ?? ''
  } catch {
    return '' // private mode: the eldest, every time
  }
})()

export const getActiveChild = () => activeChild

export function setActiveChild(id: string) {
  activeChild = id
  try {
    if (id) localStorage.setItem(CHILD_KEY, id)
    else localStorage.removeItem(CHILD_KEY)
  } catch {
    /* the selection still holds for this page */
  }
}

/** Auth and sign-up are not about a child, and Better Auth owns those paths. */
const NOT_ABOUT_A_CHILD = (path: string) =>
  path.startsWith('/api/auth/') || path.startsWith('/api/signup')

function withChild(path: string): string {
  if (!activeChild || NOT_ABOUT_A_CHILD(path)) return path
  return `${path}${path.includes('?') ? '&' : '?'}profile=${encodeURIComponent(activeChild)}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(withChild(path), init)

  /*
   * A stored child who no longer exists -- archived on another device, or
   * left behind by switching families -- would otherwise wedge the whole app
   * on a 404. Forget them and retry as the eldest, once.
   */
  if (res.status === 404 && activeChild && !NOT_ABOUT_A_CHILD(path)) {
    const peek = res.clone()
    const body = await peek.json().catch(() => null)
    if ((body as { code?: string } | null)?.code === 'no_such_profile') {
      setActiveChild('')
      return request<T>(path, init)
    }
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`
    let code: string | null = null
    let body: unknown = null
    try {
      body = await res.json()
      const parsed = body as { error?: string; code?: string }
      if (parsed.error) message = parsed.error
      if (parsed.code) code = parsed.code
    } catch {
      // Non-JSON error body; the status-based message is fine.
    }
    throw new ApiError(message, res.status, code, body)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const send = (path: string, init?: RequestInit) =>
  fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof Blob) && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  })

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const api = {
  /* auth */
  /** Who am I, with the family. Sign-in and sign-out are Better Auth's (authClient). */
  me: () => request<{ session: Session }>('/api/auth/me'),

  /* sign-up, the family that follows, invitations */
  signupConfig: () => request<SignupConfig>('/api/signup/config'),
  starterPantry: () => request<{ categories: Category[]; items: StarterItem[] }>('/api/signup/starter'),
  signUp: (input: { name: string; email: string; password: string; turnstileToken: string; callbackURL?: string }) =>
    request<{ ok: true; email: string }>('/api/signup', { method: 'POST', ...json(input) }),
  createFamily: (input: NewFamily) =>
    request<{ session: Session; invited: boolean }>('/api/families', { method: 'POST', ...json(input) }),
  invitation: (id: string) =>
    request<{ invitation: InvitationInfo }>(`/api/invitations/${encodeURIComponent(id)}`).then((r) => r.invitation),
  acceptInvitation: (id: string) =>
    request<{ session: Session }>(`/api/invitations/${encodeURIComponent(id)}/accept`, {
      method: 'POST',
      ...json({}),
    }).then((r) => r.session),

  /* feedback -- multipart, because screenshots ride along */
  sendFeedback: (form: FormData) => request<{ ok: true; id: string }>('/api/feedback', { method: 'POST', body: form }),

  /**
   * Give this week, or one day of it, to a sibling.
   *
   * A 409 carrying `code: 'needs_confirming'` is the receiving child's own
   * allergens speaking, not a failure: ask, then send it again with confirm.
   */
  copyWeekTo: (weekId: string, profileId: string, opts: { dayOfWeek?: number | null; confirm?: boolean } = {}) =>
    request<{ ok: true; childName: string; days: number[]; clashes: SiblingClash[] }>(
      `/api/weeks/${weekId}/copy-to`,
      { method: 'POST', ...json({ profileId, dayOfWeek: opts.dayOfWeek ?? null, confirm: opts.confirm ?? false }) },
    ),

  /* the children in this family */
  children: (all = false) =>
    request<{ profiles: Child[] }>(`/api/profiles${all ? '?all=1' : ''}`).then((r) => r.profiles),
  addChild: (name: string) =>
    request<{ profiles: Child[]; id: string }>('/api/profiles', { method: 'POST', ...json({ name }) }),
  renameChild: (id: string, name: string) =>
    request<{ profiles: Child[] }>(`/api/profiles/${id}`, { method: 'PATCH', ...json({ name }) }).then((r) => r.profiles),
  reorderChildren: (ids: string[]) =>
    request<{ profiles: Child[] }>('/api/profiles/reorder', { method: 'POST', ...json({ ids }) }).then((r) => r.profiles),
  archiveChild: (id: string) =>
    request<{ profiles: Child[] }>(`/api/profiles/${id}/archive`, { method: 'POST' }).then((r) => r.profiles),
  restoreChild: (id: string) =>
    request<{ profiles: Child[] }>(`/api/profiles/${id}/restore`, { method: 'POST' }).then((r) => r.profiles),

  /* admin */
  adminFamilies: () => request<{ families: AdminFamily[] }>('/api/admin/families').then((r) => r.families),
  adminFeedback: () => request<{ feedback: Feedback[] }>('/api/admin/feedback').then((r) => r.feedback),
  adminEvents: (family?: string) =>
    request<{ events: ActivityEvent[] }>(
      `/api/admin/events${family ? `?family=${encodeURIComponent(family)}` : ''}`,
    ).then((r) => r.events),
  setFeedbackStatus: (id: string, status: 'new' | 'done') =>
    request<{ ok: true }>(`/api/admin/feedback/${id}/status`, { method: 'POST', ...json({ status }) }),
  approveFamily: (id: string) =>
    request<{ ok: true }>(`/api/admin/families/${id}/approve`, { method: 'POST', ...json({}) }),
  declineFamily: (id: string) =>
    request<{ ok: true }>(`/api/admin/families/${id}/decline`, { method: 'POST', ...json({}) }),
  deleteFamily: (id: string) =>
    request<{ ok: true; accounts: number; photos: number }>(`/api/admin/families/${id}`, { method: 'DELETE' }),
  adminPantry: () => request<AdminPantry>('/api/admin/pantry'),
  familyPantry: (id: string) => request<FamilyPantryDiff>(`/api/admin/families/${id}/pantry`),
  familyWeeks: (id: string) => request<AdminFamilyWeeks>(`/api/admin/families/${id}/weeks`),
  promoteStarter: (input: { name: string; categoryId: string; emoji: string }) =>
    request<{ ok: true; item: StarterAdminItem | null; stamped: number }>('/api/admin/pantry/promote', {
      method: 'POST',
      ...json(input),
    }),
  updateStarter: (id: string, patch: { name?: string; emoji?: string; retired?: boolean }) =>
    request<{ ok: true; item: StarterAdminItem | null }>(`/api/admin/pantry/${id}`, { method: 'POST', ...json(patch) }),

  /* admin: broadcasts */
  broadcasts: () =>
    request<{ broadcasts: BroadcastSummary[] }>('/api/admin/broadcasts').then((r) => r.broadcasts),
  broadcast: (id: string) =>
    request<{ broadcast: BroadcastSummary; recipients: BroadcastRecipientRow[] }>(
      `/api/admin/broadcasts/${id}`,
    ),
  previewBroadcast: (draft: BroadcastDraft) =>
    request<BroadcastPreview>('/api/admin/broadcasts/preview', { method: 'POST', ...json(draft) }),
  testBroadcast: (draft: BroadcastDraft) =>
    request<{ ok: true; sentTo: string }>('/api/admin/broadcasts/test', {
      method: 'POST',
      ...json(draft),
    }),
  sendBroadcast: (draft: BroadcastDraft) =>
    request<{ ok: true; id: string; recipients: number }>('/api/admin/broadcasts', {
      method: 'POST',
      ...json(draft),
    }),
  optouts: () =>
    request<{ optouts: { email: string; source: string; created_at: string }[] }>(
      '/api/admin/optouts',
    ).then((r) => r.optouts),

  /* data */
  bootstrap: () => request<Bootstrap>('/api/bootstrap'),

  listWeeks: () => request<{ weeks: WeekSummary[] }>('/api/weeks').then((r) => r.weeks),
  getWeek: (id: string) => request<{ week: Week }>(`/api/weeks/${id}`).then((r) => r.week),
  ensureWeek: (startDate: string, copyFromWeekId?: string) =>
    request<{ week: Week; created: boolean }>('/api/weeks', {
      method: 'POST',
      ...json(copyFromWeekId ? { startDate, copyFromWeekId } : { startDate }),
    }),
  deleteWeek: (id: string) => request<{ ok: true }>(`/api/weeks/${id}`, { method: 'DELETE' }),

  setDay: (weekId: string, dayOfWeek: number, patch: { status?: DayStatus; label?: string }) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/days/${dayOfWeek}`, {
      method: 'PATCH',
      ...json(patch),
    }).then((r) => r.week),
  /**
   * Replace a day's contents. Each placement names the slot it goes in, which
   * for a pooled category (Side, Snack) is not always the item's own.
   */
  setDayItems: (
    weekId: string,
    dayOfWeek: number,
    placements: { itemId: string; categoryId: string }[],
  ) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/days/${dayOfWeek}/items`, {
      method: 'PUT',
      ...json({ placements }),
    }).then((r) => r.week),
  reorderDays: (weekId: string, order: number[]) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/reorder-days`, {
      method: 'POST',
      ...json({ order }),
    }).then((r) => r.week),

  /* The hand-written half. Keyed on the Monday rather than the week, so it is
     the same list from either child's board -- and every write answers with
     the whole list, because a list is what the page renders. */
  /* For a Monday with no board yet: the week's own shopping list is where a
     planned week's extras come from. */
  shoppingExtras: (startDate: string) =>
    request<{ extras: ShoppingExtra[]; extrasLastWeek: number }>(
      `/api/shopping/extras?startDate=${encodeURIComponent(startDate)}`,
    ),
  addShoppingExtra: (startDate: string, name: string) =>
    request<{ extras: ShoppingExtra[] }>('/api/shopping/extras', {
      method: 'POST',
      ...json({ startDate, name }),
    }).then((r) => r.extras),
  setShoppingExtra: (id: string, patch: { state?: ShoppingState | null; name?: string }) =>
    request<{ extras: ShoppingExtra[] }>(`/api/shopping/extras/${id}`, {
      method: 'PATCH',
      ...json(patch),
    }).then((r) => r.extras),
  removeShoppingExtra: (id: string) =>
    request<{ extras: ShoppingExtra[] }>(`/api/shopping/extras/${id}`, { method: 'DELETE' }).then(
      (r) => r.extras,
    ),
  copyShoppingExtras: (startDate: string, fromStartDate: string) =>
    request<{ extras: ShoppingExtra[]; copied: number }>('/api/shopping/extras/copy', {
      method: 'POST',
      ...json({ startDate, fromStartDate }),
    }),
  /* The whole family's list for one Monday: every child's lunches, one shop. */
  shoppingList: (startDate: string) =>
    request<ShoppingList>(`/api/shopping/list?startDate=${encodeURIComponent(startDate)}`),
  setShoppingMark: (startDate: string, itemId: string, state: ShoppingState | null) =>
    request<{ ok: true }>('/api/shopping/marks', {
      method: 'PUT',
      ...json({ startDate, itemId, state }),
    }),

  /* notes -- dayOfWeek null means the note is about the whole week */
  addNote: (weekId: string, dayOfWeek: number | null, body: string) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/notes`, {
      method: 'POST',
      ...json({ dayOfWeek, body }),
    }).then((r) => r.week),
  updateNote: (weekId: string, noteId: string, body: string) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/notes/${noteId}`, {
      method: 'PATCH',
      ...json({ body }),
    }).then((r) => r.week),
  deleteNote: (weekId: string, noteId: string) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/notes/${noteId}`, { method: 'DELETE' }).then(
      (r) => r.week,
    ),

  /* assistant */
  assistantStatus: () =>
    request<{ configured: boolean; model: string | null }>('/api/assistant'),
  /* Conversations live on the server, so both parents see the same thread. */
  listThreads: () =>
    request<{ threads: AssistantThread[] }>('/api/assistant/threads').then((r) => r.threads),
  openThread: (id: string) =>
    request<{ messages: AssistantMessage[] }>(`/api/assistant/threads/${id}`).then(
      (r) => r.messages,
    ),
  deleteThread: (id: string) =>
    request<{ threads: AssistantThread[] }>(`/api/assistant/threads/${id}`, {
      method: 'DELETE',
    }).then((r) => r.threads),
  ask: (input: {
    threadId: string | null
    message: string
    weekId: string | null
    pastWeeks: number
  }) =>
    request<{
      threadId: string
      messages: AssistantMessage[]
      threads: AssistantThread[]
      proposal: Proposal | null
      usage?: { prompt: number; completion: number }
      model: string
    }>('/api/assistant', { method: 'POST', ...json(input) }),
  /* Nothing the assistant proposes is written until this is called. */
  applyProposal: (input: { weekId: string | null; actions: ProposedChange[] }) =>
    request<{ applied: string[]; week: Week | null }>('/api/assistant/apply', {
      method: 'POST',
      ...json(input),
    }),

  /* saved meals -- a named combination of items, applied to a day in one tap */
  listMeals: () => request<{ meals: Meal[] }>('/api/meals').then((r) => r.meals),
  createMeal: (input: {
    name: string
    notes?: string
    emoji?: string
    favorite?: boolean
    itemIds?: string[]
    fromWeekId?: string
    dayOfWeek?: number
  }) =>
    request<{ id: string; meals: Meal[] }>('/api/meals', { method: 'POST', ...json(input) }),
  updateMeal: (
    id: string,
    patch: { name?: string; notes?: string; emoji?: string; favorite?: boolean; itemIds?: string[] },
  ) =>
    request<{ meals: Meal[] }>(`/api/meals/${id}`, { method: 'PATCH', ...json(patch) }).then(
      (r) => r.meals,
    ),
  deleteMeal: (id: string) =>
    request<{ meals: Meal[] }>(`/api/meals/${id}`, { method: 'DELETE' }).then((r) => r.meals),
  applyMeal: (weekId: string, dayOfWeek: number, mealId: string, mode: 'replace' | 'add') =>
    request<{ week: Week }>(`/api/weeks/${weekId}/days/${dayOfWeek}/apply-meal`, {
      method: 'POST',
      ...json({ mealId, mode }),
    }).then((r) => r.week),

  /* review handoff -- `deliveries` reports what actually reached an inbox */
  requestReview: (weekId: string, payload: { message: string; editNoteId: string | null }) =>
    request<{ week: Week; deliveries: Delivery[] }>(`/api/weeks/${weekId}/review/request`, {
      method: 'POST',
      ...json(payload),
    }),
  approveReview: (weekId: string, comment: string) =>
    request<{ week: Week; deliveries: Delivery[] }>(`/api/weeks/${weekId}/review/approve`, {
      method: 'POST',
      ...json({ comment }),
    }),
  reopenReview: (weekId: string) =>
    request<{ week: Week }>(`/api/weeks/${weekId}/review/reopen`, {
      method: 'POST',
      ...json({}),
    }).then((r) => r.week),

  /* people and notification settings */
  listPeople: () => request<{ people: Person[] }>('/api/people').then((r) => r.people),
  addPerson: (input: {
    name: string
    email: string
    phone?: string
    notifyReminder?: boolean
    notifyReview?: boolean
  }) =>
    request<{ people: Person[] }>('/api/people', { method: 'POST', ...json(input) }).then(
      (r) => r.people,
    ),
  updatePerson: (
    id: string,
    patch: { name?: string; email?: string; phone?: string; notifyReminder?: boolean; notifyReview?: boolean },
  ) =>
    request<{ people: Person[] }>(`/api/people/${id}`, { method: 'PATCH', ...json(patch) }).then(
      (r) => r.people,
    ),
  deletePerson: (id: string) =>
    request<{ people: Person[] }>(`/api/people/${id}`, { method: 'DELETE' }).then((r) => r.people),
  /** Invite a contact to plan together, or send the invitation again. */
  invitePerson: (id: string) =>
    request<{ people: Person[]; resent: boolean }>(`/api/people/${id}/invite`, { method: 'POST', ...json({}) }),

  notifications: () =>
    request<{ reminder: ReminderSettings; emailConfigured: boolean; log: EmailLogEntry[] }>(
      '/api/notifications',
    ),
  setReminder: (patch: Partial<ReminderSettings>) =>
    request<{ reminder: ReminderSettings }>('/api/notifications/reminder', {
      method: 'PUT',
      ...json(patch),
    }).then((r) => r.reminder),
  runReminderNow: () =>
    request<{ run: ReminderRun }>('/api/notifications/run-reminder', {
      method: 'POST',
      ...json({}),
    }).then((r) => r.run),
  sendTestEmail: (personId: string) =>
    request<{ ok: true; recipient: string }>('/api/notifications/test', {
      method: 'POST',
      ...json({ personId }),
    }),

  /* bulk add -- parse suggests, nothing is created until the rows are confirmed */
  bulkParse: (text: string, defaultCategoryId: string) =>
    request<{ rows: BulkRow[]; usedAi: boolean }>('/api/items/bulk/parse', {
      method: 'POST',
      ...json({ text, defaultCategoryId }),
    }),
  bulkAdd: (items: { name: string; categoryId: string; emoji: string; notes: string }[]) =>
    request<{ added: Item[] }>('/api/items/bulk', { method: 'POST', ...json({ items }) }).then(
      (r) => r.added,
    ),

  recentItems: () => request<{ itemIds: string[] }>('/api/items/recent').then((r) => r.itemIds),
  createItem: (input: {
    categoryId: string
    name: string
    notes?: string
    emoji?: string
    allergens?: ItemAllergen[]
    allergensChecked?: boolean
  }) => request<{ item: Item }>('/api/items', { method: 'POST', ...json(input) }).then((r) => r.item),
  updateItem: (
    id: string,
    patch: {
      name?: string
      notes?: string
      emoji?: string
      categoryId?: string
      archived?: boolean
      allergens?: ItemAllergen[]
      allergensChecked?: boolean
    },
  ) => request<{ item: Item }>(`/api/items/${id}`, { method: 'PATCH', ...json(patch) }).then((r) => r.item),

  /** Replace the child's allergens and school rules. The editor sends the whole list. */
  saveAllergens: (allergens: NewProfileAllergen[]) =>
    request<{ allergens: ProfileAllergen[] }>('/api/profile/allergens', {
      method: 'PUT',
      ...json({ allergens }),
    }).then((r) => r.allergens),
  deleteItem: (id: string) =>
    request<{ ok: true; removedFromMeals: number }>(`/api/items/${id}`, { method: 'DELETE' }),

  /**
   * Archive, unarchive or delete a batch. 'kept' names the foods a delete
   * refused, because a planned week still references them.
   */
  batchItems: (ids: string[], action: 'delete' | 'archive' | 'unarchive') =>
    request<{
      changed: string[]
      kept: { id: string; name: string; usedCount: number }[]
      removedFromMeals: number
    }>('/api/items/batch', { method: 'POST', ...json({ ids, action }) }),

  uploadImage: (id: string, blob: Blob) =>
    request<{ imageKey: string }>(`/api/items/${id}/image`, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': blob.type },
    }).then((r) => r.imageKey),
  removeImage: (id: string) => request<{ ok: true }>(`/api/items/${id}/image`, { method: 'DELETE' }),
}

export const imageUrl = (key: string): string => `/img/${key}`
